import React, { useState } from 'react';
import { Button, Tag, Tooltip, Image, Popover } from 'antd';
import { UpOutlined, DownOutlined, PaperClipOutlined } from '@ant-design/icons'
import moment from 'moment';
import _ from 'lodash';

import { types, repeatObjects, verifyConditional, renderPlaceholder } from '../helper';
import i18next from 'i18next';
import Geolocation from './geolocation';
import { numberWithCommas } from '../../../components/DynamicViewNew/helper';
import { CustomIcon, FormPopover, GetIcon } from 'datastake-daf/dist/components';
import { getSelectOptions, propHasValue } from '../../../components/DynamicFormNew/helper';
import { getImageUploadViewValue } from '../../../helpers/form';
import TooltipIcon from '../../../components/dashboards/components/TooltipIcon';
import DataLink from './DataLink';
import { showHideInput } from '../../EditTIF/helper';
import Versions from './Versions';
import Comments from './Comments';

export const getLabel = (label, allData) => {
  if (label && typeof label === 'object') {
    let toReturn = label;

    Object.keys(label).forEach((k) => {
      const show = showHideInput(
        { showIf: k },
        allData
      )

      if (show) {
        toReturn = label[k];
      }
    })

    return toReturn;
  }

  return label;
};

export const BasicInput = ({
  parent = null,
  name = null,
  config = {},
  data = {},
  allData = {},
  linkingData = {},
  style = {},
  ajaxOptions,
  className = '',
  cols,
  versionsDatapoints,
  repeatable = false,
  labelsOnly = false,
  valueOnly = false,
  actionActive = false,
  repeatableIndex = null,
  onAction = () => { },
  formData,
  valueOnlyString
}) => {
  const [extraOpen, setExtraOpen] = useState(false);
  const fullInputs = ['checkboxGroup', 'imageUpload'];

  const label = (input = config) => {
    const lbl = input.outputLabel ? input.outputLabel : input.label;
    switch (typeof lbl) {
      case 'string':
        return lbl;
      case 'object':
        const hasData = Array.isArray(data) ? !!data.length : !!Object.keys(data).length;
        const labelKey = hasData ? (Object.keys(lbl).find(cond =>
          verifyConditional(cond, Array.isArray(data) ? data[0] : data, allData)) || Object.keys(lbl)[0]) : Object.keys(lbl)[0];
        return lbl[labelKey];
      default:
        return lbl;
    }
  }

  const comment = () => {
    return ((data?.meta?.inputs || {})[name] || {}).comment;
  }

  const placeholder = (item, name, withPlaceholder = true) =>
    renderPlaceholder({ data, config, name, withPlaceholder, parent });

  const select = (item, input = config, inputName, withPlaceholder = true) => {
    if (typeof input.options === 'string') {
      const [formName, key] = input.options.split('.');
      const d = formData[formName] || [];
      const option = d.find((d) => d[key] === (item ? item[inputName] : data[inputName]));
      return option && option.name
        ? option.name
        : placeholder(item || data, inputName, withPlaceholder)
    } else {
      let option = (Array.isArray(input.options) ? input.options : [])
        .find(opt =>
          opt.value === (item ? item[inputName] : data[inputName]));
      if (option) {
        option = getSelectOptions(option, data);
      }
      return option && option.label
        ? option.label
        : placeholder(item || data, inputName, withPlaceholder)
    }
  }

  const multiselect = (item, input = config, inputName, withPlaceholder = true) => {
    const options = (input.options || [])
      .filter(opt => ((item ? item[inputName] : data[inputName]) || []).includes(opt.value));

    return options && options.length > 0 ? options.map(opt => {
      if (opt) {
        opt = getSelectOptions(opt, data);
      }
      return (
        <Tag key={opt.label}>{opt.label}</Tag>
      )
    }) : placeholder(item || data, inputName, withPlaceholder);
  }

  const radioGroup = (item, input = config, inputName, withPlaceholder = true) => {
    const selectedItem = item ? item[inputName] : data[inputName];
    const selectedOption = propHasValue(selectedItem) ? input.options.find((o) => o.value === selectedItem) : undefined;
    const label = propHasValue(selectedOption) ? selectedOption.label : null;
    return label || placeholder(item || data, inputName, withPlaceholder);
  }

  const ajaxSelect = (item, input = config, inputName, withPlaceholder = true) => {
    try {
      let option;
      const linkConfig = input.dataLink ? input.dataLink : (input.dataView ? input.dataView : {});

      if (input?.meta?.onNewSetValueKey && data[input?.meta?.onNewSetValueKey]) {
        option = {
          label: data[input?.meta?.onNewSetValueKey],
          value: data[input?.meta?.onNewSetValueKey],
        }
      } else if (linkConfig && linkConfig.formNamespace) {
        const [, , mapper] = (input?.meta?.call || '').split('::');
        const linkigD = linkingData ? linkingData[linkConfig.formNamespace] : {};
        let inputData;
        if (inputName !== 'parent') {
          inputData = linkigD
            ? linkigD[item ? item[inputName] : data[inputName]]
            : null;
        } else {
          inputData = item ? item : data;
        }
        option = Object.keys(JSON.parse(mapper))
          .reduce((opt, key) => {
            opt[key] = inputData ? inputData[JSON.parse(mapper)[key]] : null;
            return opt;
          }, {});
      } else {
        option = ajaxOptions.find(opt =>
          opt.value === (item ? item[inputName] : data[inputName]));
      }

      return option && option.label
        ? option.label
        : (withPlaceholder ? <span className="text-muted">{i18next.t('Not answered')}</span> : '')
    } catch (err) {
      return <span className="text-muted">{i18next.t('Not answered')}</span>;
    }
  }

  const total100 = (item, input = config, inputName, withPlaceholder = true) => {
    const inputData = item ? item[inputName] : data[inputName];
    const { options } = input;
    const values = (inputData || []).map(d => {
      const option = options.find(o => o.value === d.type);
      if (option) {
        return {
          type: option.label,
          value: d.value,
        };
      }
      return null;
    });
    return values.length ? values.map(val => (
      val && val.type ? <Tag>{val.type}: {val.value}%</Tag> : placeholder(val, inputName, withPlaceholder)
    )) : placeholder(inputData, inputName, withPlaceholder);
  }

  const imageUpload = (item, input = config, inputName, withPlaceholder = true) => {
    const value = item ? (propHasValue(item[inputName]) ? item[inputName] : placeholder(item, inputName, withPlaceholder))
      : (propHasValue(data[inputName]) ? data[inputName] : placeholder(data, inputName, withPlaceholder));
    let fileList = getImageUploadViewValue(value);

    return fileList.length ? <div className="image-container">
      {fileList.filter(f => f.url).map((v, i) => (
        <div className="image-cont" key={`image-${i}`}>
          <Image
            width={88}
            src={v.url}
          />
        </div>
      ))}
      {/* {previewData ? <Modal
        width={1200}
        footer={null}
        visible={previewOpen}
        title={null}
        maskClosable={true}
        closable={false}
        onClose={() => setPreviewOpen(false)}
        className="preview-modal">
        <CloseOutlined style={{color:'white', position: 'absolute', right: '10px', top: '10px', fontSize: '22px'}} onClick={() => {
          setPreviewOpen(false);
          setPreviewData(null);
        }}/>
        <img style={{maxWidth: '1200px', objectFit: 'contain'}} src={previewData.response.url}/>
      </Modal> : null} */}
    </div> : typeof value !== 'object' ? value : placeholder(data, inputName, withPlaceholder);
  };

  const notUndefined = (item) => propHasValue(item);

  const getValue = (input, inputName, item, withPlaceholder = true) => {
    switch (input.type) {
      case 'text':
      case 'phoneNumber':
      case 'textarea':
        return item
          ? (propHasValue(item[inputName]) ? item[inputName] : placeholder(item, inputName, withPlaceholder))
          : (propHasValue(data[inputName]) ? data[inputName] : placeholder(data, inputName, withPlaceholder));
      case 'link':
        const v = item
          ? (propHasValue(item[inputName]) ? item[inputName] : placeholder(item, inputName, withPlaceholder))
          : (propHasValue(data[inputName]) ? data[inputName] : placeholder(data, inputName, withPlaceholder))
        return <a href={v} target="_blank" rel="noreferrer">{v}</a>;
      case 'number':
        return item
          ? (propHasValue(item[inputName]) ? numberWithCommas(item[inputName]) : placeholder(item, inputName, withPlaceholder))
          : (propHasValue(data[inputName]) ? numberWithCommas(data[inputName]) : placeholder(data, inputName, withPlaceholder));
      case 'percentage':
        return item
          ? (propHasValue(item[inputName]) ? item[inputName] + '%' : placeholder(item, inputName, withPlaceholder))
          : (propHasValue(data[inputName]) ? data[inputName] + '%' : placeholder(data, inputName, withPlaceholder));
      case 'select':
        return select(item, input, inputName, withPlaceholder);
      case 'ajaxSelect':
        return ajaxSelect(item, input, inputName, withPlaceholder);
      case 'switch':
        return i18next.t(item && propHasValue(item[inputName])
          ? (item[inputName] ? 'Yes' : input.noValue || 'No')
          : (data[inputName] ? 'Yes' : input.noValue || 'No'));
      case 'modal':
        return data.stakeholderId || 'modal';
      case 'dataLink':
        return (
          <DataLink
            form={input}
            values={data}
            formData={data[inputName] || []}
            k={inputName}
          />
        );
      case 'button':
        return <Button
          ghost
          type="primary"
          size="small"
          className='no-ln-height'
          icon={actionActive ? <UpOutlined /> : <DownOutlined />}
          onClick={() => onAction(repeatableIndex)}>
          {actionActive ? i18next.t('View Less') : i18next.t('View More')}
        </Button>
      case 'multiselect':
        return multiselect(item, input, inputName, withPlaceholder);
      case 'radioGroup':
        return radioGroup(item, input, inputName, withPlaceholder);
      case 'checkboxGroup':
        const options = (config.options || [])
          .filter(opt => ((item ? item[inputName] : data[inputName]) || [])
            .includes(opt.value))
          .map(opt => opt.value);
        if (config.yesNoTag) {
          const opt = options[0];
          const val = opt || false;
          return (
            <Tag
              color={val ? 'blue' : 'defaut'}
              style={{
                width: 60,
                textAlign: 'center',
                color: !val && '#595959',
                border: !val && '1px solid #D9D9D9',
              }}
            >
              {val ? i18next.t('Yes') : i18next.t('No')}
            </Tag>
          );
        }
        return options && options.length > 0 ? <div className="checkboxGroup-opt">
          {config.options.map(opt => (
            <div className="opt" key={opt.label}>
              <span>{opt.label}</span>
              <GetIcon
                icon={options.includes(opt.value) ? 'CheckOutlined' : 'CloseOutlined'}
                style={{
                  color: options.includes(opt.value) ? 'green' : 'red'
                }} />
            </div>
          ))}
        </div> : placeholder(item || data, inputName, withPlaceholder)
      case 'date':
        return item
          ? (propHasValue(item[inputName]) ? moment(item[inputName]).format('DD MMM YYYY').toString() : placeholder(item, inputName, withPlaceholder))
          : (propHasValue(data[inputName]) ? moment(data[inputName]).format('DD MMM YYYY').toString() : placeholder(data, inputName, withPlaceholder));
      case 'year':
        return item
          ? (propHasValue(item[inputName]) ? moment(item[inputName]).format('YYYY').toString() : placeholder(item, inputName, withPlaceholder))
          : (propHasValue(data[inputName]) ? moment(data[inputName]).format('YYYY').toString() : placeholder(data, inputName, withPlaceholder));
      case 'geolocation':
        return item
          ? (propHasValue(item[inputName]) ? <Geolocation val={item[inputName]} /> : placeholder(item, inputName, withPlaceholder))
          : (propHasValue(data[inputName]) ? <Geolocation val={data[inputName]} /> : placeholder(data, inputName, withPlaceholder));
      case 'tag':
        return item
          ? (propHasValue(item[inputName]) ? item[inputName].map(tag => <Tag>{tag}</Tag>) : placeholder(item, inputName, withPlaceholder))
          : (propHasValue(data[inputName]) ? data[inputName].map(tag => <Tag>{tag}</Tag>) : placeholder(data, inputName, withPlaceholder));
      case 'total100':
        return total100(item, input, inputName, withPlaceholder);
      case 'upload':
        try {
          const renderTags = (tag, i) => (
            <div className="view-ant-upload-list-item" key={`tag-${i}`}>
              <div className="ant-upload-icon">
                <PaperClipOutlined />
              </div>
              <a href={tag.url} className="ant-upload-list-item-name">
                {tag.name}
              </a>
            </div>
          )
          return item
            ? (propHasValue(item[inputName]) ? item[inputName].map(renderTags) : placeholder(item, inputName, withPlaceholder))
            : (propHasValue(data[inputName]) ? data[inputName].map(renderTags) : placeholder(data, inputName, withPlaceholder))
        } catch (e) {
          console.log(e);
          return '';
        }
      case 'imageUpload':
        return imageUpload(item, input, inputName, withPlaceholder);
      default:
        return input.type;
    }
  }

  const value = (item, input = config) => {
    switch (input.type) {
      case 'groupInputs':
        return repeatObjects(config.inputs, (key, i) => {
          const first = i === 0;
          let oLabel = '';
          if (config.inputs[key].showOutputLabel) {
            oLabel = label(config.inputs[key]);
          }
          return item
            ? (notUndefined(item[key]) ? <span key={`${key}-${i}`}>{getValue(config.inputs[key], key, item, false)}{oLabel}&nbsp;</span> : (first ? placeholder(item, name, true) : ''))
            : (notUndefined(data[key]) ? <span key={`${key}-${i}`}>{getValue(config.inputs[key], key, data, false)}{oLabel}&nbsp;</span> : (first ? placeholder(data, name, true) : ''))
        });
      case 'group':
        return repeatObjects(config.inputs, (key, i) => {
          const first = i === 0;
          let oLabel = '';
          if (config.inputs[key].showOutputLabel) {
            oLabel = label(config.inputs[key]);
          }
          return item
            ? (notUndefined(item[key]) ? <span key={`${key}-${i}`}>{getValue(config.inputs[key], key, item, false)}{oLabel}&nbsp;</span> : (first ? placeholder(item, name, true) : ''))
            : (notUndefined(data[key]) ? <span key={`${key}-${i}`}>{getValue(config.inputs[key], key, data, false)}{oLabel}&nbsp;</span> : (first ? placeholder(data, name, true) : ''))
        });
      default:
        return getValue(input, name, item);
    }
  }

  const inputComment = comment();

  if (valueOnlyString) {
    return value();
  }

  if (config.type === 'header') {
    const _subTitle = config.viewSubTitle || config.subTitle

    return (
      <div className="daf-title with-subtitle">
        <h1>{getLabel(config.label, allData)}</h1>
        {_subTitle ? typeof _subTitle === 'string' ? (
          <p>{_subTitle}</p>
        ) : <p>{getLabel(_subTitle, allData)}</p> : null}
      </div>
    )
  }

  return !valueOnly ? <div
    className={`input ${className} ${name === 'action' ? 'action' : ''}`}
  >
    <p className="label">
      <div className='flex'>
        <span>{label()}</span>
        {!!config.description ? (
          <Popover
            content={(
              <div className="max-w-200">
                {config.description}
              </div>
            )}
            placement="top"
          >
            <div className="flex flex-column ml-1">
              <TooltipIcon fontSize={13} />
            </div>
          </Popover>
        ) : null}
      </div>

      {config.meta?.code ? (
        <span className="code">{config?.meta?.code}</span>
      ) : null}
    </p>

    {!repeatable ?
      name === 'action' ?
        <Button
          ghost
          type="primary"
          size="small"
          onClick={() => setExtraOpen(!extraOpen)}>
          {i18next.t('View More')}
        </Button>
        : (
          <div className="flex">
            <div className="flex-1">
              <p className="value">{value()}</p>
            </div>
            <div className="ml-4 input-actions flex">
              <Versions versionsDatapoints={versionsDatapoints} config={config} allData={allData} name={name} getValue={getValue} />

              <Button className="default p-0 flex flex-column justify-content-center" type="link" disabled>
                <CustomIcon name="Sources" width={16} height={16} />
              </Button>

              <Comments config={config} allData={allData} name={name} />
            </div>
          </div>
        )
      : !labelsOnly ? (
        (data).map(d => (
          <React.Fragment>
            <p className="value">
              {value(d)}
            </p>
            {extraOpen ? <div style={{ width: '100%', height: '200px' }}>
            </div> : null}
          </React.Fragment>
        ))
      ) : null}
  </div> : (
    <div
      className={`input ${className} ${name === 'action' ? 'action' : ''}`}
      style={
        Object.assign(style, {
          width: fullInputs.includes(config.type) ? '100%' : `calc(100% / ${cols} - ${!repeatable ? '10px' : '0px'})`,
        })
      }> <p className="value">
        {value()} {config.comment && inputComment ? (
          <Tooltip placement="top" title={inputComment}>
            <Button
              style={{
                color: inputComment ? 'var(--mmt-primary-70)' : 'none',
              }}
              disabled={!inputComment}
              icon={
                <GetIcon style={{
                  color: inputComment ? 'var(--mmt-primary-70)' : 'none',
                  marginLeft: '8px'
                }}
                  icon={inputComment ? 'chat-quote-fill' : 'chat-quote-line'} />
              }
              type="link"
              size="small">
            </Button>
          </Tooltip>
        ) : null}
      </p>
    </div>
  );

}

const Input = ({
  name = null,
  parent = null,
  config = {},
  data = {},
  className,
  versionsDatapoints,
  allData = {},
  linkingData = {},
  linkingForms = {},
  ajaxOptions = [],
  extraInputs = null,
  cols = 4,
  extraCols = 4,
  isRepeatable = false,
}) => {
  const excludeKeys = ['position', 'label', 'id'];
  const [hasOpenedExtra, setHasOpenedExtra] = useState(null);
  const sortTableCols = (form, sortKey) => {
    return Object.keys(form)
      .filter(key => propHasValue(form[key][sortKey]))
      .sort((a, b) => {
        return form[a][sortKey] - form[b][sortKey];
      })
      .reduce((f, key) => {
        if (propHasValue(form[key][sortKey])) {
          f[key] = form[key];
        }
        return f;
      }, {});
  }

  const getExtraTableCols = (form) => {
    const cols = Object.keys(form)
      .filter(key => propHasValue(form[key].table));
    return Object.keys(form)
      .filter(key => !cols.includes(key))
      .reduce((f, key) => {
        f[key] = form[key];
        return f;
      }, {});
  }

  const lastTableCol = (form) => {
    return Object.keys(form)
      .filter(key => propHasValue(form[key].table)).length
  }

  const filterForm = (form, data) => {
    if (!form.moduleScope) {
      return Object.keys(form).reduce((f, key) => {
        if (verifyConditional(form[key].showIf, data ? (parent && data[parent] ? data[parent] : data) : undefined, allData)) {
          f[key] = form[key];
        }
        return f;
      }, {})
    }
    return form;
  }

  const getModalContent = () => {
    const defaultKey = Object.keys(linkingForms[parent])[0];
    const linkingD = linkingData ? (linkingData[config.dataLink.formNamespace] || {}) : {};
    const modalData = data[parent] ? (
      !Array.isArray(data[parent])
        ? typeof data[parent] === 'string'
          ? linkingD[data[parent]]
          : (linkingD[data[parent][name]] || {})
        : (data[parent].map(item => {
          if (item[name]) {
            Object.assign(item, {
              ...linkingD[item[name]]
            });
          }
          return item;
        }) || {})) : (isRepeatable ? [] : {});
    const form = Object.keys(linkingForms[parent][defaultKey])
      .reduce((f, key) => {
        const input = linkingForms[parent][defaultKey][key];
        if (!excludeKeys.includes(key)) {
          if (input.showIf && !propHasValue(input.table)) {
            if (verifyConditional(input.showIf, modalData, modalData)) {
              f[key] = linkingForms[parent][defaultKey][key];
            }
          } else {
            f[key] = linkingForms[parent][defaultKey][key];
          }
        }
        return f;
      }, {});
    if (extraInputs) {
      return getRepeatableContent(Object.assign(form, { ...extraInputs }), modalData);
    }
    if (isRepeatable) {
      const extraRepeatCols = getExtraTableCols(form);
      const hasExtraFields = isRepeatable && extraRepeatCols
        && Object.keys(extraRepeatCols).length;
      if (hasExtraFields) {
        Object.assign(config, {
          action: {
            label: '',
            type: 'button',
            table: lastTableCol(config),
          },
        });
      }
    }
    const formCfg = isRepeatable
      ? sortTableCols(form, 'table')
      : form;
    const totalCols = Object.keys(formCfg).length;
    const _length = Object.keys(formCfg || {}).length;
    const isEven = _length % 2 === 0;

    return !isRepeatable ? (
      <React.Fragment>
        {repeatObjects(formCfg, (key, ind) => {
          const dataId = formCfg[key].dataId || key;
          return <BasicInput
            parent={name}
            key={key}
            name={dataId}
            config={form[key]}
            versionsDatapoints={versionsDatapoints}
            data={modalData ? modalData : (isRepeatable ? [] : {})}
            linkingData={linkingData}
            allData={data}
            style={(ind + 1) % 2 ? { marginRight: '16px' } : {}}
            cols={cols}
            repeatable={isRepeatable}
            className={className !== 'last' ? undefined : (
              isEven ? (
                (ind === _length - 1 || ind === _length - 2) ? 'last' : undefined
              ) : ind === _length - 1 ? 'last' : undefined)}
            ajaxOptions={ajaxOptions}>
          </BasicInput>
        })}
        {isRepeatable && Array.isArray(modalData) && !modalData.length ? (
          <span className="text-muted">{i18next.t('Not answered')}</span>
        ) : null}
      </React.Fragment>
    ) :
      <React.Fragment>
        {
          repeatObjects(formCfg, (key, ind) => {
            const dataId = formCfg[key].dataId || key;
            const _length = Object.keys(formCfg || {}).length;
            const isEven = _length % 2 === 0;
            // className={
            //   isEven ? (
            //     (ind === _length - 1 || ind === _length - 2) ? 'last' : undefined
            //   ) : ind === _length - 1 ? 'last' : undefined}
            return <BasicInput
              versionsDatapoints={versionsDatapoints}
              key={key}
              name={dataId}
              config={formCfg[key]}
              data={modalData ? modalData : (isRepeatable ? [] : {})}
              allData={data}
              repeatable={isRepeatable}
              cols={isRepeatable ? totalCols : 4}
              labelsOnly={true}
              ajaxOptions={ajaxOptions}
              className={
                isEven ? (
                  (ind === _length - 1 || ind === _length - 2) ? 'last' : undefined
                ) : ind === _length - 1 ? 'last' : undefined}
              linkingData={linkingData}>
            </BasicInput>
          })
        }
        {
          (!modalData || (Array.isArray(modalData) && !modalData.length)) ? (
            <span className="text-muted">{i18next.t('Not answered')}</span>
          ) : (
            (Array.isArray(modalData) ? modalData : [modalData]).map((item, itemId) => (
              <div className={`repeatable-row ${itemId === hasOpenedExtra ? 'open' : ''}`} key={itemId}>
                {repeatObjects(formCfg, (key, ind) => {
                  const _length = Object.keys(formCfg || {}).length;
                  const isEven = _length % 2 === 0;
                  // className={
                  //   isEven ? (
                  //     (ind === _length - 1 || ind === _length - 2) ? 'last' : undefined
                  //   ) : ind === _length - 1 ? 'last' : undefined}
                  return (
                    <BasicInput
                      key={key}
                      versionsDatapoints={versionsDatapoints}
                      name={formCfg[key].dataId || key}
                      linkingData={linkingData}
                      config={formCfg[key]}
                      data={item}
                      allData={data}
                      className={`${ind === 0 ? 'first-col' : ''} ${isEven ? (
                        (ind === _length - 1 || ind === _length - 2) ? 'last' : ''
                      ) : ind === _length - 1 ? 'last' : ''
                        }`}
                      repeatable={isRepeatable}
                      cols={isRepeatable ? totalCols : 4}
                      valueOnly={true}
                      repeatableIndex={itemId}
                      actionActive={itemId === hasOpenedExtra}
                      ajaxOptions={ajaxOptions}>
                    </BasicInput>
                  )
                })}
              </div>
            ))
          )
        }
      </React.Fragment>;
  }

  const getRepeatableContent = (cForm, cData) => {
    const formConfig = cForm ? cForm : config;
    const formData = cData ? cData : data;
    const hasExtraFields = isRepeatable
      && Object.keys(getExtraTableCols(formConfig)).length;
    if (hasExtraFields) {
      Object.assign(formConfig, {
        action: {
          label: '',
          type: 'button',
          table: lastTableCol(formConfig),
        },
      });
    }
    const formCfg = isRepeatable
      ? sortTableCols(formConfig, 'table')
      : formConfig;
    const totalCols = Object.keys(formCfg).length;
    return <React.Fragment>
      {repeatObjects(formCfg, (key, ind) => {
        const dataId = formCfg[key].dataId || key;
        const _length = Object.keys(formCfg || {}).length;
        const isEven = _length % 2 === 0;
        // className={
        //   isEven ? (
        //     (ind === _length - 1 || ind === _length - 2) ? 'last' : undefined
        //   ) : ind === _length - 1 ? 'last' : undefined}
        return <BasicInput
          key={key}
          name={dataId}
          versionsDatapoints={versionsDatapoints}
          className={
            isEven ? (
              (ind === _length - 1 || ind === _length - 2) ? 'last' : undefined
            ) : ind === _length - 1 ? 'last' : undefined}
          allData={allData}
          config={formCfg[key]}
          data={formData}
          repeatable={isRepeatable}
          cols={isRepeatable ? totalCols : 4}
          labelsOnly={true}
          ajaxOptions={ajaxOptions}
          linkingData={linkingData}>
        </BasicInput>
      })}
      {(!formData || (Array.isArray(formData) && !formData.length)) ? (
        <span className="text-muted">{i18next.t('Not answered')}</span>
      ) : (
        (Array.isArray(formData) ? formData : [formData]).map((item, itemId) => (
          <React.Fragment key={itemId}>
            <div className={`repeatable-row ${itemId === hasOpenedExtra ? 'open' : ''}`}>
              {repeatObjects(formCfg, (key, i) => {
                const _length = Object.keys(formCfg || {}).length;
                const isEven = _length % 2 === 0;
                // className={
                //   isEven ? (
                //     (ind === _length - 1 || ind === _length - 2) ? 'last' : undefined
                //   ) : ind === _length - 1 ? 'last' : undefined}
                return (
                  <BasicInput
                    key={key}
                    versionsDatapoints={versionsDatapoints}
                    name={formConfig[key].dataId || key}
                    linkingData={linkingData}
                    config={formConfig[key]}
                    data={item}
                    formData={allData}
                    allData={data}
                    className={`${i === 0 ? 'first-col' : ''} ${isEven ? (
                      (i === _length - 1 || i === _length - 2) ? 'last' : ''
                    ) : i === _length - 1 ? 'last' : ''
                      }`}
                    repeatable={isRepeatable}
                    cols={isRepeatable ? totalCols : 4}
                    valueOnly={true}
                    repeatableIndex={itemId}
                    actionActive={itemId === hasOpenedExtra}
                    ajaxOptions={ajaxOptions}
                    onAction={(ev) => {
                      if (ev === hasOpenedExtra) {
                        setHasOpenedExtra(null);
                      } else {
                        setHasOpenedExtra(ev)
                      }
                    }}>
                  </BasicInput>
                )
              })}
            </div>
            <div className="repetable-row-extra" style={{
              height: hasOpenedExtra === itemId ? 'auto' : '0',
              padding: hasOpenedExtra === itemId ? '16px' : '0',
              borderLeft: hasOpenedExtra === itemId ? '1px solid #91d5ff' : 'none',
              // borderRight: hasOpenedExtra === itemId ? '1px solid #91d5ff' : 'none',
              // borderBottom: hasOpenedExtra === itemId ? '1px solid #91d5ff' : 'none',
              borderBottomLeftRadius: '5px',
              borderBottomRightRadius: '5px'
            }}>
              {repeatObjects(getExtraTableCols(formConfig), (key, ind) => {
                return verifyConditional(formConfig[key].showIf, item, allData) ? <BasicInput
                  versionsDatapoints={versionsDatapoints}
                  key={key}
                  name={formConfig[key].dataId || key}
                  config={formConfig[key]}
                  data={item}
                  linkingData={linkingData}
                  allData={data}
                  repeatable={false}
                  className="bb-none"
                  cols={extraCols}
                  ajaxOptions={ajaxOptions}>
                </BasicInput> : null;
              })}
            </div>
          </React.Fragment>
        ))
      )}
    </React.Fragment>
  }

  const getComponent = () => {
    const fData = parent ? (config.dataId ? data[config.dataId] : data[parent]) : data;
    const form = filterForm(config, fData);
    const allFData = Object.assign(_.clone(fData) || {}, { ...data });
    switch (config.type) {
      case types.MODAL:
        if (config.showIf) {
          return verifyConditional(config.showIf, allFData, allData) ? getModalContent() : null;
        }
        return getModalContent();
      default:
        return isRepeatable
          ? getRepeatableContent()
          : <React.Fragment>
            {config.moduleScope ? (
              verifyConditional(config.showIf, allFData, allData) ? <BasicInput
                linkingData={linkingData}
                versionsDatapoints={versionsDatapoints}
                key={name}
                name={config.dataId || name}
                config={config}
                data={parent ? data[parent] : data}
                allData={data}
                repeatable={false}
                className={className}
                cols={2}
                ajaxOptions={ajaxOptions}
              /> : null
            ) : repeatObjects(form, (key, ind) => {
              const _length = Object.keys(form || {}).length;
              const isEven = _length % 2 === 0;

              return (
                <BasicInput
                  versionsDatapoints={versionsDatapoints}
                  linkingData={linkingData}
                  key={`${key}-${ind}-${Date.now()}`}
                  name={config[key].dataId || key}
                  className={
                    isEven ? (
                      (ind === _length - 1 || ind === _length - 2) ? 'last' : undefined
                    ) : ind === _length - 1 ? 'last' : undefined}
                  config={config[key]}
                  data={data}
                  allData={data}
                  repeatable={false}
                  cols={2}
                  ajaxOptions={ajaxOptions}
                />
              )
            })}
          </React.Fragment>
    }
  }

  return getComponent();
}

export default Input;
